// RequestStatusTable.jsx
import React, { useState } from 'react';
import { Table, Message, Button, Popup, Icon, Menu, Label, Header } from 'semantic-ui-react';
import WithdrawalModal from './WithdrawalModal';
import WithdrawalSuccessModal from './WithdrawalSuccessModal';
import RequestDetailsModal from './RequestDetailsModal';
import EditRequestModal from './EditRequestModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import FilterPanel from './FilterPanel';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { useRequestFilters } from '../hooks/useRequestFilters';
import { useTimezone } from '../hooks/useTimezone';
import { formatStatus, getStatusColor, getLocationDisplayName, isRequestForm3, highlightSearchTerms, getCloudName, getSubmittedBy, getBatchBadges } from '../utils/requestHelpers';
import { formatTimestamp } from '../utils/timezone.utils';
import axios from 'axios';

const RequestStatusTable = ({ requests, onRefresh, userRoles = [], userEmail, userCategories = [] }) => {
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [showWithdrawalSuccessModal, setShowWithdrawalSuccessModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const [infoBanner, setInfoBanner] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [openPopupId, setOpenPopupId] = useState(null); // Track which popup is open by request ID

    // Use reusable hooks
    const { rowsPerPage, tableDensity, activePage, setActivePage } = useTablePreferences();
    const userTimezone = useTimezone(); // Listen to timezone changes for re-renders
    const {
        statusFilter,
        cloudNameFilter,
        categoryFilter,
        requestIdFilter,
        setStatusFilter,
        setCloudNameFilter,
        setCategoryFilter,
        setRequestIdFilter,
        hasActiveFilters,
        filteredRequests,
        resetFilters
    } = useRequestFilters(requests);

    // Sorting state for main requests table
    const [sortColumn, setSortColumn] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('descending'); // 'ascending' | 'descending'

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(column);
            // Default to descending for timestamp, ascending otherwise
            setSortDirection(column === 'timestamp' ? 'descending' : 'ascending');
        }
    };

    // Reset page when filters change
    const handleFilterChange = () => {
        setActivePage(1);
    };

    const handleResetFilters = () => {
        resetFilters();
        setActivePage(1);
    };

    // Early returns after all hooks
    if (!requests || requests.length === 0) {
        return (
            <Message info>
                <Message.Header>No Requests Found</Message.Header>
                <p>You haven't submitted any requests yet.</p>
            </Message>
        );
    }

    if (filteredRequests.length === 0 && hasActiveFilters) {
        return (
            <div className="page-fade-in" style={{
                marginTop: '4px',
                overflowX: 'auto',
                padding: '0.8rem 1rem',
                marginLeft: '-150px',
                marginRight: '-150px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Header
                        as="h3"
                        style={{
                            margin: 0,
                            color: '#062D4D',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Icon name="list" style={{ color: '#35B7F4' }} />
                        <Header.Content>Your Request Status</Header.Content>
                    </Header>
                    <Button
                        icon
                        labelPosition="left"
                        onClick={() => setShowFilters(!showFilters)}
                        style={{ marginLeft: 'auto' }}
                    >
                        <Icon name={showFilters ? 'filter' : 'filter'} />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                </div>
                {showFilters && (
                    <FilterPanel
                        statusFilter={statusFilter}
                        cloudNameFilter={cloudNameFilter}
                        categoryFilter={categoryFilter}
                        requestIdFilter={requestIdFilter}
                        onStatusFilterChange={setStatusFilter}
                        onCloudNameFilterChange={setCloudNameFilter}
                        onCategoryFilterChange={setCategoryFilter}
                        onRequestIdFilterChange={setRequestIdFilter}
                        onResetFilters={handleResetFilters}
                        hasActiveFilters={hasActiveFilters}
                        onFilterChange={handleFilterChange}
                    />
                )}
                <Message info>
                    <Message.Header>No matching requests found</Message.Header>
                    <p>No records match the selected filters. Try adjusting your filters.</p>
                </Message>
            </div>
        );
    }

    const canWithdraw = (status) => {
        // Allow withdrawal only for pending requests
        return status === 'PENDING_APPROVER';
    };

    const canEdit = (request) => {
        // Can only edit if status is PENDING_APPROVER (before approver approval)
        return request.status === 'PENDING_APPROVER';
    };

    const isSuccessfullyCreated = (request) => {
        const status = String(request?.status || '').toLowerCase();
        if (!status) return false;

        // Exclude known non-success terminal states
        if (status.includes('failed') || status.includes('error') || status.includes('rejected') || status.includes('withdrawn')) {
            return false;
        }
        if (status.includes('cancelled') || status.includes('canceled')) {
            return false;
        }

        // Include known success-ish terminal states
        return status.includes('ready') || status.includes('completed') || status.includes('created');
    };

    const buildJiraCreateUrl = (request) => {
        const base = (process.env.REACT_APP_JIRA_CREATE_URL || 'https://crashplan.atlassian.net/jira/software/c/projects/PLDEVOPS/issues/').trim();
        if (!base) return null;

        const cloudName = getCloudName(request);
        const submittedBy = getSubmittedBy(request);
        const statusText = formatStatus(request?.status);

        // Works with many Jira "Create Issue" endpoints that accept `summary` / `description` as query params.
        const summary = `[tenantBridge] Request ${request?.id || ''} - ${cloudName}`;
        const description =
            `Request ID: ${request?.id || 'N/A'}\n` +
            `Cloud Name: ${cloudName}\n` +
            `Submitted By: ${submittedBy}\n` +
            `Status: ${statusText}\n\n` +
            `Please describe the issue and attach any relevant logs/screenshots.`;

        const url = new URL(base);
        url.searchParams.set('summary', summary);
        url.searchParams.set('description', description);
        return url.toString();
    };

    const handleRaiseJiraClick = (request) => {
        const url = buildJiraCreateUrl(request);
        if (!url) {
            setInfoBanner('JIRA URL is not configured. Set REACT_APP_JIRA_CREATE_URL to enable ticket creation.');
            setTimeout(() => setInfoBanner(''), 3500);
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleAccessKeeperClick = () => {
        const keeperUrl = process.env.REACT_APP_KEEPER_URL || 'https://keepersecurity.com/vault/';
        window.open(keeperUrl, '_blank', 'noopener,noreferrer');
    };


    const handleWithdrawClick = (request) => {
        setSelectedRequest(request);
        setShowWithdrawalModal(true);
        setError('');
    };

    const handleViewDetailsClick = (request) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const handleEditClick = (request) => {
        setSelectedRequest(request);
        setShowEditModal(true);
        setError('');
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
        setSelectedRequest(null);
        setError('');
    };

    const handleEditSuccess = () => {
        // Refresh the requests list after successful edit
        if (onRefresh) {
            onRefresh();
        }
    };

    const copyToClipboard = async (text, successMessage = 'Copied to clipboard') => {
        try {
            await navigator.clipboard.writeText(String(text));
            setInfoBanner(successMessage);
            setTimeout(() => setInfoBanner(''), 2500);
        } catch (e) {
            setError('Copy failed');
            setTimeout(() => setError(''), 2500);
        }
    };

    const canDelete = (status) => {
        // Can only delete if status is PENDING_APPROVER (before approver approval)
        return status === 'PENDING_APPROVER';
    };

    const handleDeleteClick = (request) => {
        setSelectedRequest(request);
        setShowDeleteModal(true);
        setError('');
        setOpenPopupId(null); // Close the popup when delete is clicked
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRequest) return;

        try {
            setIsDeleting(true);
            setError('');
            const response = await axios.delete(`https://qa-tenantbridge.parablu.com/api/requests/${selectedRequest.id}`, {
                withCredentials: true
            });

            if (response.data.success) {
                setShowDeleteModal(false);
                setInfoBanner('Request deleted successfully');
                setTimeout(() => setInfoBanner(''), 2500);
                // Refresh the requests list after successful deletion
                if (onRefresh) {
                    onRefresh();
                }
            } else {
                setError(response.data.message || 'Failed to delete request');
            }
        } catch (err) {
            console.error('Error deleting request:', err);
            setError(err.response?.data?.message || 'Failed to delete request');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setSelectedRequest(null);
        setError('');
    };

    // Calculate sorted and paginated requests
    const sortedRequests = (filteredRequests || []).slice().sort((a, b) => {
        const directionFactor = sortDirection === 'descending' ? -1 : 1;

        const getFieldValue = (req, column) => {
            switch (column) {
                case 'timestamp':
                    return new Date(req.timestamp).getTime() || 0;
                case 'requestId':
                    return String(req.id || '').toLowerCase();
                case 'cloudName':
                    return getCloudName(req).toLowerCase();
                case 'category':
                    return String(req.category || '').toLowerCase();
                case 'location':
                    return getLocationDisplayName(req.formData.location?.location).toLowerCase();
                case 'status':
                    return String(req.status || '').toLowerCase();
                default:
                    return '';
            }
        };

        const aVal = getFieldValue(a, sortColumn);
        const bVal = getFieldValue(b, sortColumn);

        if (aVal < bVal) return -1 * directionFactor;
        if (aVal > bVal) return 1 * directionFactor;
        return 0;
    });
    const totalPages = Math.max(1, Math.ceil(sortedRequests.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleRequests = sortedRequests.slice(pageStart, pageEnd);

    const handleWithdrawSubmit = async (reason) => {
        if (!selectedRequest) return;

        try {
            setIsWithdrawing(true);
            setError('');

            const response = await axios.post('https://qa-tenantbridge.parablu.com/api/withdraw-request', {
                requestId: selectedRequest.id,
                withdrawalReason: reason
            }, {
                withCredentials: true
            });

            if (response.data.success) {
                setShowWithdrawalModal(false);
                setShowWithdrawalSuccessModal(true);
                // Store the approval status and details for the success modal
                setSelectedRequest({
                    ...selectedRequest,
                    withdrawalUser: response.data.withdrawalUser
                });
                // Refresh the requests by calling the parent component's refresh function
                if (onRefresh) {
                    onRefresh();
                }
            } else {
                setError(response.data.message || 'Failed to withdraw request');
            }
        } catch (err) {
            console.error('Error withdrawing request:', err);
            setError(err.response?.data?.message || 'Failed to withdraw request');
        } finally {
            setIsWithdrawing(false);
        }
    };

    const handleWithdrawalModalClose = () => {
        setShowWithdrawalModal(false);
        setSelectedRequest(null);
        setError('');
    };

    const handleWithdrawalSuccessClose = () => {
        setShowWithdrawalSuccessModal(false);
        setSelectedRequest(null);
    };

    const handleDetailsModalClose = () => {
        setShowDetailsModal(false);
        setSelectedRequest(null);
    };

    return (
        <div className="page-fade-in" style={{
            marginTop: '4px',
            padding: '0.8rem 1rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 200px)',
            minHeight: '600px'
        }}>
            {/* Fixed Header */}
            <div style={{
                flexShrink: 0,
                backgroundColor: 'white',
                zIndex: 100,
                paddingBottom: '0.5rem',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '1.35rem',
                            fontWeight: 700,
                            color: '#062D4D'
                        }}
                    >
                        <Icon name="list" color="blue" />
                        <span>Your Request Status</span>
                    </div>
                    <Button
                        icon
                        labelPosition="left"
                        onClick={() => setShowFilters(!showFilters)}
                        style={{ marginLeft: 'auto' }}
                        color={showFilters ? 'blue' : undefined}
                        primary={hasActiveFilters && !showFilters}
                    >
                        <Icon name="filter" />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                        {hasActiveFilters && !showFilters && (
                            <Label circular color="blue" style={{ marginLeft: '8px' }}>
                                {[statusFilter, cloudNameFilter, categoryFilter, requestIdFilter].filter(v => v).length}
                            </Label>
                        )}
                    </Button>
                </div>
                <div style={{
                    fontSize: '0.9em',
                    color: '#666',
                    marginBottom: '0.25rem',
                    fontStyle: 'italic'
                }}>
                    ← Scroll horizontally to view all columns →
                </div>
            </div>

            {/* Fixed Filter Section */}
            {showFilters && (
                <div style={{ flexShrink: 0, marginBottom: '16px' }}>
                    <FilterPanel
                        statusFilter={statusFilter}
                        cloudNameFilter={cloudNameFilter}
                        categoryFilter={categoryFilter}
                        requestIdFilter={requestIdFilter}
                        onStatusFilterChange={setStatusFilter}
                        onCloudNameFilterChange={setCloudNameFilter}
                        onCategoryFilterChange={setCategoryFilter}
                        onRequestIdFilterChange={setRequestIdFilter}
                        onResetFilters={handleResetFilters}
                        hasActiveFilters={hasActiveFilters}
                        onFilterChange={handleFilterChange}
                    />
                </div>
            )}

            {/* Scrollable Table Container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                minHeight: 0
            }}>
                {error && (
                    <Message negative style={{ marginBottom: '1rem' }}>
                        {error}
                    </Message>
                )}
                {infoBanner && (
                    <Message positive style={{ marginBottom: '1rem' }}>
                        {infoBanner}
                    </Message>
                )}
                <Table celled unstackable sortable compact={tableDensity === 'compact'} size={tableDensity === 'compact' ? 'small' : undefined} style={{
                    width: '100%',
                    minWidth: '2000px',
                    margin: '0 10px'
                }}>
                    <Table.Header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <Table.Row>
                            <Table.HeaderCell
                                sorted={sortColumn === 'timestamp' ? sortDirection : null}
                                onClick={() => handleSort('timestamp')}
                                style={{ width: '150px', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                                Timestamp
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortColumn === 'requestId' ? sortDirection : null}
                                onClick={() => handleSort('requestId')}
                                style={{ width: '150px', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                                Request ID
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortColumn === 'cloudName' ? sortDirection : null}
                                onClick={() => handleSort('cloudName')}
                                style={{ width: '150px', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                                Cloud Name
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortColumn === 'category' ? sortDirection : null}
                                onClick={() => handleSort('category')}
                                style={{ width: '100px', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                                Category
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                sorted={sortColumn === 'location' ? sortDirection : null}
                                onClick={() => handleSort('location')}
                                style={{ width: '120px', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                                Location
                            </Table.HeaderCell>

                            <Table.HeaderCell
                                sorted={sortColumn === 'status' ? sortDirection : null}
                                onClick={() => handleSort('status')}
                                style={{ width: '200px', backgroundColor: 'white', cursor: 'pointer' }}
                            >
                                Current Status
                            </Table.HeaderCell>
                            <Table.HeaderCell style={{ width: '200px', backgroundColor: 'white' }}>Approved By</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: '200px', backgroundColor: 'white' }}>Current Approver(s)</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: '200px', backgroundColor: 'white' }}>Rejected By</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: '700px', backgroundColor: 'white' }}>Rejection Reason</Table.HeaderCell>
                            <Table.HeaderCell style={{ width: '300px', backgroundColor: 'white' }}>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {visibleRequests.map(request => {
                            // Collect all active search terms for highlighting
                            const searchTerms = [
                                statusFilter,
                                cloudNameFilter,
                                categoryFilter,
                                requestIdFilter
                            ].filter(term => term && term.trim());
                            
                            return (
                                <Table.Row key={request.id}>
                                    <Table.Cell>
                                        {highlightSearchTerms(formatTimestamp(request.timestamp), searchTerms)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(request.id, searchTerms)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(getCloudName(request), searchTerms)}
                                        {getBatchBadges(request).map((badge, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    marginLeft: idx === 0 ? '8px' : '4px',
                                                    padding: '2px 6px',
                                                    backgroundColor: '#e1f5fe',
                                                    color: '#35B7F4',
                                                    borderRadius: '3px',
                                                    fontSize: '0.8em',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {badge}
                                            </span>
                                        ))}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(
                                            request.category === 'PROD' &&
                                            request.formData?.requestForm3
                                                ? 'PROD / POC'
                                                : request.category,
                                            searchTerms
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(getLocationDisplayName(request.formData.location?.location), searchTerms)}
                                    </Table.Cell>

                                    <Table.Cell className={getStatusColor(request.status)}>
                                        {highlightSearchTerms(formatStatus(request.status), searchTerms)}
                                        {request.status === 'PENDING_APPROVER' && (
                                            <div style={{
                                                fontSize: '0.8em',
                                                fontStyle: 'italic',
                                                marginTop: '4px',
                                                color: '#666'
                                            }}>
                                                After approval, Tenant creation may take upto 20 minutes
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(request.status === 'REJECTED' ? '-' : request.approver || '-', searchTerms)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {request.currentApprover
                                            ? request.currentApprover.split(',').map((email, index) => (
                                                <div key={index}>
                                                    {highlightSearchTerms(email.trim(), searchTerms)}
                                                    <br />
                                                </div>
                                            ))
                                            : highlightSearchTerms('-', searchTerms)}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(
                                            request.status === 'REJECTED'
                                                ? (request.approverComment
                                                    ? request.approver
                                                    : '-')
                                                : '-',
                                            searchTerms
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {highlightSearchTerms(
                                            request.status === 'REJECTED'
                                                ? (request.approverComment || '-')
                                                : request.status === 'WITHDRAWN'
                                                    ? (request.approverComment || '-')
                                                    : '-',
                                            searchTerms
                                        )}
                                    </Table.Cell>
                                    <Table.Cell style={{ width: '300px', whiteSpace: 'nowrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Button
                                            size="small"
                                            content="View"
                                            onClick={() => handleViewDetailsClick(request)}
                                        />

                                        {isSuccessfullyCreated(request) ? (
                                            <>
                                                {/* Show JIRA button for PROD or CUST category in ST form, or PROD category in MT form with isPOC=false */}
                                                {((request.category === 'PROD' || request.category === 'CUST') && !isRequestForm3(request)) ||
                                                (request.category === 'PROD' && isRequestForm3(request) && !request.formData?.isPOC) ? (
                                                    <Button
                                                        size="small"
                                                        color="blue"
                                                        onClick={() => handleRaiseJiraClick(request)}
                                                    >
                                                        Request Credentials via JIRA
                                                    </Button>
                                                ) : null}
                                                {/* Show Keeper button for all other categories */}
                                                {(request.category !== 'PROD' && request.category !== 'CUST') || 
                                                 (request.category === 'PROD' && isRequestForm3(request) && request.formData?.isPOC) ||
                                                 (request.category === 'CUST' && isRequestForm3(request)) ? (
                                                    <Button
                                                        size="small"
                                                        color="green"
                                                        onClick={handleAccessKeeperClick}
                                                    >
                                                        Access Credentials via Keeper
                                                    </Button>
                                                ) : null}
                                            </>
                                        ) : (
                                            <>
                                                {canWithdraw(request.status) ? (
                                                    <Button
                                                        size="small"
                                                        negative
                                                        loading={isWithdrawing && selectedRequest?.id === request.id}
                                                        disabled={isWithdrawing}
                                                        onClick={() => handleWithdrawClick(request)}
                                                    >
                                                        Withdraw
                                                    </Button>
                                                ) : (
                                                    <Popup
                                                        content="Withdraw is only available while pending approval"
                                                        position="top center"
                                                        trigger={<div><Button size="small" disabled>Withdraw</Button></div>}
                                                    />
                                                )}

                                                {canEdit(request) ? (
                                                    <Button
                                                        size="small"
                                                        primary
                                                        onClick={() => handleEditClick(request)}
                                                    >
                                                        Edit
                                                    </Button>
                                                ) : (
                                                    <Popup
                                                        content="Editing is only available while pending approval"
                                                        position="top center"
                                                        trigger={<div><Button size="small" disabled>Edit</Button></div>}
                                                    />
                                                )}
                                            </>
                                        )}

                                    <Popup
                                            on='click'
                                            position='bottom right'
                                            pinned
                                            mouseLeaveDelay={0}
                                            offset={[0, 8]}
                                            style={{ zIndex: 2000 }}
                                            open={openPopupId === request.id}
                                            onOpen={() => setOpenPopupId(request.id)}
                                            onClose={() => setOpenPopupId(null)}
                                            trigger={<Button size="small" icon><Icon name="ellipsis vertical" /></Button>}
                                            content={
                                                <Menu vertical secondary size='small' style={{ minWidth: 180, fontSize: '0.9em' }}>
                                                    <Menu.Item style={{ padding: '6px 10px' }} onClick={() => {
                                                        copyToClipboard(request.id, 'Request ID copied');
                                                        setOpenPopupId(null); // Close popup after copying
                                                    }}>
                                                        <Icon name='copy' size='small' /> Copy ID
                                                    </Menu.Item>
                                                    <Menu.Item disabled style={{ padding: 0 }}>
                                                        <div style={{ height: 1, background: '#e5e7eb', margin: '4px 0' }} />
                                                    </Menu.Item>
                                                    {canDelete(request.status) ? (
                                                        <Menu.Item
                                                            style={{ padding: '6px 10px', color: '#CE1B4E' }}
                                                            onClick={() => handleDeleteClick(request)}
                                                        >
                                                            <Icon name='trash' size='small' /> Delete
                                                        </Menu.Item>
                                                    ) : (
                                                        <Popup
                                                            content='Deletion is only available while pending approval'
                                                            position='left center'
                                                            on='hover'
                                                            style={{ background: '#ffffff', color: '#062D4D', border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                                            trigger={
                                                                <div>
                                                                    <Menu.Item
                                                                        style={{ padding: '6px 10px' }}
                                                                        disabled
                                                                    >
                                                                        <Icon name='trash' size='small' /> Delete
                                                                    </Menu.Item>
                                                                </div>
                                                            }
                                                        />
                                                    )}
                                                </Menu>
                                            }
                                        />
                                    </div>

                                    {request.status === 'WITHDRAWN' && (
                                        <div style={{
                                            marginTop: '8px',
                                            color: '#666',
                                            fontStyle: 'italic',
                                            fontSize: '0.9em'
                                        }}>
                                            Withdrawn
                                        </div>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </div>

            {/* Fixed Pagination */}
            {filteredRequests.length > 0 && (
                <PaginationFooter
                    activePage={activePage}
                    totalPages={totalPages}
                    totalItems={requests.length}
                    filteredItems={sortedRequests}
                    hasActiveFilters={hasActiveFilters}
                    onPageChange={setActivePage}
                    pageStart={pageStart}
                    pageEnd={pageEnd}
                />
            )}

            <WithdrawalModal
                open={showWithdrawalModal}
                onClose={handleWithdrawalModalClose}
                onSubmit={handleWithdrawSubmit}
                isSubmitting={isWithdrawing}
                requestData={selectedRequest}
            />

            <WithdrawalSuccessModal
                open={showWithdrawalSuccessModal}
                onClose={handleWithdrawalSuccessClose}
                requestData={selectedRequest}
            />

            <RequestDetailsModal
                open={showDetailsModal}
                onClose={handleDetailsModalClose}
                request={selectedRequest}
            />

            <EditRequestModal
                open={showEditModal}
                onClose={handleEditModalClose}
                request={selectedRequest}
                userEmail={userEmail}
                userRoles={userRoles}
                userCategories={userCategories}
                onSuccess={handleEditSuccess}
            />

            <DeleteConfirmationModal
                open={showDeleteModal}
                onClose={handleDeleteModalClose}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
                requestData={selectedRequest}
            />

        </div>
    );
};

export default RequestStatusTable;